import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '@/context/UserContext';
import { LayoutDashboard, Settings, LogOut, Shield, Target, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '@/App';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useUser();
  const [expiringCount, setExpiringCount] = useState(0);

  const fetchExpiringUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      const now = new Date();
      const threeDays = 3 * 24 * 60 * 60 * 1000;
      const count = res.data.filter(u => {
        if (u.is_deleted || !u.access_expires_at || u.auto_renew) return false;
        const exp = new Date(u.access_expires_at);
        return exp > now && (exp - now) < threeDays;
      }).length;
      setExpiringCount(count);
    } catch {}
  }, [isAdmin]);

  useEffect(() => { fetchExpiringUsers(); }, [fetchExpiringUsers]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const navBtn = (path, active) =>
    `gap-2 text-sm ${active ? 'bg-emerald-700 text-white hover:bg-emerald-800' : 'text-slate-300 hover:text-white hover:bg-white/10'}`;

  return (
    <nav className="bg-[#1A4D2E] border-b border-[#153d24] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          <div className="flex items-center gap-8">
            <div className="cursor-pointer flex items-center" onClick={() => navigate('/')}>
              <span className="text-lg font-bold text-white tracking-wider">SSCaraïbes.com</span>
            </div>
            <div className="flex gap-1">
              <Button variant="ghost" onClick={() => navigate('/')} className={navBtn('/', location.pathname === '/')} data-testid="nav-dashboard-btn">
                <LayoutDashboard className="h-4 w-4" />
                Dashboard
              </Button>
              <Button variant="ghost" onClick={() => navigate('/prospection')} className={navBtn('/prospection', location.pathname.startsWith('/prospection'))} data-testid="nav-prospection-btn">
                <Target className="h-4 w-4" />
                Prospection
              </Button>
              <Button variant="ghost" onClick={() => navigate('/campaigns')} className={navBtn('/campaigns', location.pathname.startsWith('/campaigns'))} data-testid="nav-campaigns-btn">
                <Megaphone className="h-4 w-4" />
                Campagnes
              </Button>
              <Button variant="ghost" onClick={() => navigate('/settings')} className={navBtn('/settings', location.pathname === '/settings')} data-testid="nav-settings-btn">
                <Settings className="h-4 w-4" />
                Parametres
              </Button>
              {isAdmin && (
                <Button variant="ghost" onClick={() => navigate('/admin')} className={`${navBtn('/admin', location.pathname === '/admin')} relative`} data-testid="nav-admin-btn">
                  <Shield className="h-4 w-4" />
                  Admin
                  {expiringCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center rounded-full bg-orange-500 text-white text-[10px] font-bold animate-pulse" data-testid="expiring-badge">
                      {expiringCount}
                    </span>
                  )}
                </Button>
              )}
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout} className="gap-2 text-slate-300 hover:text-white hover:bg-white/10 text-sm" data-testid="logout-btn">
            <LogOut className="h-4 w-4" />
            Deconnexion
          </Button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
